import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

export const Logo = ({ size = 'lg', invert = true }) => {
  const sizeClasses = {
    sm: 'h-6',
    md: 'h-8',
    lg: 'h-10 md:h-12',
    xl: 'h-14 md:h-16'
  };
  return (
    <img
      src="/logo.png"
      alt="The NRI Realtor"
      className={sizeClasses[size]}
      data-testid="logo"
    />
  );
};

export const Navbar = () => {
  const [visible, setVisible] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > window.innerHeight * 0.85);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const handleNavClick = (href) => {
    setMenuOpen(false);
    if (href.startsWith('#')) {
      setTimeout(() => document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' }), 100);
    } else {
      navigate(href);
    }
  };

  return (
    <>
      <AnimatePresence>
        {visible && (
          <motion.nav
            data-testid="navbar"
            initial={{ y: -100 }}
            animate={{ y: 0 }}
            exit={{ y: -100 }}
            transition={{ duration: 0.4 }}
            className="fixed top-0 left-0 right-0 z-50 bg-[#0B3D2E] shadow-lg"
          >
            <div className="max-w-7xl mx-auto px-6 md:px-12 py-3 flex items-center justify-between">
              <div className="cursor-pointer" onClick={() => { navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); setMenuOpen(false); }}>
                <Logo size="lg" />
              </div>

              <div className="hidden md:flex items-center gap-8">
                <a href="#builders" className="text-sm text-white/80 hover:text-white transition-colors" data-testid="nav-link-builders">Projects</a>
                <a href="#services" className="text-sm text-white/80 hover:text-white transition-colors" data-testid="nav-link-services">Services</a>
                <a href="#about" className="text-sm text-white/80 hover:text-white transition-colors" data-testid="nav-link-about">About Us</a>
                <a href="#enquiry" className="text-sm text-white/80 hover:text-white transition-colors" data-testid="nav-link-enquiry">Enquiry</a>
              </div>

              <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden p-2 text-white" data-testid="mobile-menu-button">
                {menuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 pt-20 bg-[#0B3D2E] md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col items-center gap-8 px-8 py-12">
              <button onClick={() => handleNavClick('#builders')} className="text-2xl font-heading font-light text-white hover:text-brand-gold-light transition-colors" data-testid="mobile-nav-projects">Projects</button>
              <button onClick={() => handleNavClick('#services')} className="text-2xl font-heading font-light text-white hover:text-brand-gold-light transition-colors" data-testid="mobile-nav-services">Services</button>
              <button onClick={() => handleNavClick('#enquiry')} className="text-2xl font-heading font-light text-white hover:text-brand-gold-light transition-colors" data-testid="mobile-nav-enquiry">Enquiry</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
